/**
 * EstimatedResponsibility — one upcoming-visit precollect card.
 *
 * Renders only in the precollect flow (when the URL contains
 * `:appointmentId`). Hidden in balance-pay where there's no upcoming visit.
 *
 * **Same-day appointments (Phase 1, build plan §3.2).** The card no
 * longer reads the page-level scalars off the `/checkin/balance`
 * response; it takes ONE `SameDayAppointment` line as `data`, and
 * `PayPage` renders one card per same-day appointment (anchor first).
 * When the backend is older and omits `same_day_appointments`, PayPage
 * synthesizes a single line from the legacy scalars, so this component
 * never needs to know which backend it is talking to.
 *
 * **Same-day batch charge (Phase 2, build plan §4.5).** The header
 * checkbox means "include this visit in today's payment". `PayPage`
 * keeps the set of checked cards and submits ONE `POST /checkin/payment`
 * whose `lines[]` carries every checked card with its full
 * `estimate_remaining_minor_units`, plus the prior balance — one card
 * entry, one Stripe confirmation, N `PAYMENT` rows on the claims side.
 * Every chargeable card is checked by default. The body is a single
 * "Due today  $X" line (patient-portal issue 1, 2026-10-01) — the old
 * explanatory paragraph cost two-three wrapped lines per card.
 *
 * **Phase 0.7.0+ behavior:** the patient cannot pay a partial estimate.
 * They pay either the full remaining estimate or nothing. Two reasons
 * (per `Patient-Payment-Portal-Backend-v0.7.0` handoff):
 *
 *   1. Stranded sub-Stripe-minimum remainders — a partial pay could leave
 *      $0.30 owed, which Stripe can't charge.
 *   2. Front-desk discretion is the right escalation path if the patient
 *      genuinely can't pay the full estimate.
 *
 * **v0.7.0 — display fields rebuild.** Per QA Section A.2 (and Backend
 * v0.7.1's confirmation that 7 new fields are live on /checkin/balance):
 *
 *   - Appointment-type name, right-aligned under "Total estimate".
 *     Provider and facility are deliberately NOT shown (patient-portal
 *     issue 1, 2026-09-30) — they cost two rows per card for no decision
 *     value on a same-day pay screen.
 *   - Breakdown block (deductible / copay / coinsurance / insurance-pay)
 *     between the header and the action row. Coinsurance is raw varchar
 *     ("20%", "$30") — displayed as-is per backend's "we don't parse,
 *     you don't parse" instruction. Zero values omitted to reduce noise.
 *   - Info ("i") tooltip next to the section title per QA BUG-004,
 *     explaining what "Estimated Responsibility" means.
 *
 * States:
 *
 *   P. `has_estimate === false` — "pending, no estimate yet". Decision
 *      D3: always shown, never hidden. No checkbox, money suppressed.
 *   A. `estimate_remaining === 0` (with an estimate) — "settled". In the
 *      single-appointment case PayPage skips the card entirely (legacy
 *      behaviour); in a multi-visit day it is shown so the patient sees
 *      the whole day's picture.
 *   B. `0 < estimate_remaining < 50` cents — un-actionable info card.
 *   C. `estimate_remaining >= 50` cents — checkbox + summary.
 */
import { useEffect, useId } from 'react';
import { Info } from 'lucide-react';

import { Alert } from '@tensaw/design-system/feedback';
import { Card, CardContent } from '@tensaw/design-system/layout';
import { Checkbox, Label } from '@tensaw/design-system/primitives';

import type { SameDayAppointment } from '../../api/types';
import { format } from '../../lib/money';
import { STRIPE_USD_MIN_CENTS } from './stripeMinimum';

// Re-exported for the existing import path (`PayPage`, tests).
export { STRIPE_USD_MIN_CENTS } from './stripeMinimum';

const ESTIMATE_TOOLTIP_COPY =
  "Your share of the visit cost based on your insurance benefits. " +
  "Includes any deductible, copay, and coinsurance the clinic estimates " +
  "you'll owe. Final amount may change after your insurance processes " +
  "the claim.";

interface EstimatedResponsibilityProps {
  /** One same-day appointment line (anchor or sibling). */
  data: SameDayAppointment;
  /** True when THIS visit's full estimate is included in the charge. */
  selected: boolean;
  onSelectedChange: (next: boolean) => void;
}

/**
 * Coinsurance is a raw varchar from the CRM (`"20%"`, `"$30"`, `"0.0"`,
 * `"20% of allowed"`, `""`, or null). Backend deliberately doesn't parse
 * it and explicitly tells FE not to parse either — display as-is. But
 * v0.8.3 QA flagged that rendering `0.0` literally looks broken: it has
 * no `%` or `$` to ground it, and from a patient's perspective "what's
 * 0.0?" is a fair question.
 *
 * Strategy: suppress the row entirely when the value parses to numeric
 * zero. Does NOT reformat non-zero values — `"$30"` still renders as
 * `"$30"`, `"20% of allowed"` still renders verbatim. Only the
 * meaningless-zero case is suppressed.
 *
 * Returns true for: `null`, `""`, `"0"`, `"0.0"`, `"0.00"`, `"0%"`,
 * `"$0"`, `"$0.00"`, `"  0  "`. Returns false for any value that
 * contains a non-zero digit or non-numeric text content.
 */
function isCoinsuranceZeroOrEmpty(raw: string | null): boolean {
  if (raw === null) return true;
  const trimmed = raw.trim();
  if (trimmed === '') return true;
  const stripped = trimmed.replace(/[$%\s]/g, '');
  const asNumber = Number(stripped);
  if (Number.isNaN(asNumber)) return false; // non-numeric content — render verbatim
  return asNumber === 0;
}

/**
 * A bare number (`"20"`, `"10.0"`) gets a `%` suffix; anything already
 * carrying its own unit or text (`"20%"`, `"$30"`, `"20% of allowed"`)
 * renders verbatim, so it never shows as `"20%%"` or `"$30%"`.
 */
function formatCoinsurance(raw: string): string {
  const trimmed = raw.trim();
  return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}%` : trimmed;
}

export function EstimatedResponsibility({
  data,
  selected,
  onSelectedChange,
}: EstimatedResponsibilityProps) {
  const checkboxId = useId();
  const tooltipId = useId();
  const pending = !data.has_estimate;
  const remaining = data.estimate_remaining_minor_units;
  const paid = data.estimate_paid_minor_units;
  const total = data.estimate_total_minor_units;
  const settled = !pending && remaining === 0;
  const subMinimum = !pending && remaining > 0 && remaining < STRIPE_USD_MIN_CENTS;
  // Mirrors `isLineChargeable` — a cancelled line (normally absent from
  // the list; the backend rejects money on it) is never selectable.
  const selectable = !pending && !settled && !subMinimum && !data.appointment_cancelled;

  // If the line can't be charged (pending / settled / sub-minimum), force
  // selected=false so the patient can't accidentally try to pay it.
  // Defensive: PayPage's buildRequest also rejects this, but a stale
  // `selected: true` from a prior estimate state could leak through if
  // the value changed mid-session (e.g. front-desk applied a payment).
  useEffect(() => {
    if (!selectable && selected) onSelectedChange(false);
  }, [selectable, selected, onSelectedChange]);

  const hasBreakdown = !pending && breakdownRows(data).length > 0;

  return (
    <Card data-testid={`estimate-card-${data.appointment_id}`}>
      <CardContent className="p-0">
        {/* Header — row 1: title + total/paid; row 2: visit date (never
            wraps) + appointment type right-aligned under the total. */}
        <div className="border-b border-border px-4 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div className="flex items-start gap-2 sm:gap-3">
              {selectable && (
                <Checkbox
                  id={checkboxId}
                  checked={selected}
                  onCheckedChange={(next: boolean | 'indeterminate') => {
                    onSelectedChange(next === true);
                  }}
                  aria-label={
                    data.appointment_type_name
                      ? `Include ${data.appointment_type_name} in this payment`
                      : 'Include this visit in this payment'
                  }
                  className="mt-1"
                />
              )}
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor={selectable ? checkboxId : undefined}
                  className="text-[15px] font-semibold sm:text-base text-foreground"
                >
                  Estimated Responsibility
                </Label>
                {/* Per QA BUG-004: info button explaining what this
                    section means. Native title attribute is the
                    cheapest accessible tooltip — design-system has no
                    Tooltip primitive (would mean upstream work for
                    one micro-interaction). aria-describedby attaches
                    the same copy for screen readers. */}
                <button
                  type="button"
                  aria-label="About estimated responsibility"
                  aria-describedby={tooltipId}
                  title={ESTIMATE_TOOLTIP_COPY}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                <span id={tooltipId} className="sr-only">
                  {ESTIMATE_TOOLTIP_COPY}
                </span>
              </div>
            </div>
            <div className="shrink-0 text-right text-sm text-muted-foreground">
              {pending ? (
                <div>
                  Estimate{' '}
                  <span className="font-semibold text-foreground">pending</span>
                </div>
              ) : (
                <>
                  {/* Amount only — no visible "Total estimate" label; the
                      card's "Due today" line restates it, and the label
                      cost the title its single line on a phone. The
                      sr-only text keeps it meaningful to screen readers. */}
                  <div className="text-base font-semibold text-primary tabular-nums">
                    <span className="sr-only">Total estimate </span>
                    {format(total)}
                  </div>
                  {paid > 0 && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                      Already paid {format(paid)}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* Row 2 spans the full card width (no indent under the title)
              so the date line has room and doesn't word-wrap on a phone —
              "Upcoming visit. September 30, 2026 at 9:18 AM" is ~251px
              against ~278px of content at 360px. The appointment type
              drops to its own right-aligned line when it doesn't fit
              beside the date. Below 360px the date may wrap rather than
              overflow the card. */}
          <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            <p className="min-[360px]:whitespace-nowrap">
              {/*
                v0.8.4 / backend v0.8.2: render the pre-formatted
                clinic-local string verbatim. DO NOT pass through
                Intl.DateTimeFormat or `new Date()` — the browser's
                timezone is irrelevant, what matters is the clinic's
                local time, which only the backend knows.
                Per ledger #49.
              */}
              Upcoming visit. {data.appointment_date_formatted}
            </p>
            {data.appointment_type_name && (
              <p className="ml-auto text-right">{data.appointment_type_name}</p>
            )}
          </div>
        </div>

        {/* Breakdown block — only when at least one breakdown field is
            populated. Lets the patient see what makes up the estimate. */}
        {hasBreakdown && (
          <BreakdownBlock data={data} />
        )}

        {/* Action area */}
        <div className="px-4 py-5 sm:px-6">
          {pending ? (
            <PendingNotice />
          ) : settled ? (
            <SettledNotice />
          ) : subMinimum ? (
            <SubMinimumNotice remainingCents={remaining} />
          ) : (
            <CheckboxRowSummary remainingCents={remaining} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Estimate breakdown — Deductible, Copay, Coinsurance, Insurance Pay.
 *
 * Each row omitted when its value is empty per the testing-team A.2 spec
 * ("If estimated insurance pay is $0, just omit that line"). Coinsurance
 * is raw varchar per Backend v0.7.1 Item 4 — displayed verbatim, no parse.
 *
 * Copay wins over coinsurance: when a visit carries both, only Copay is
 * shown (patient-portal issue 1, 2026-09-30) — the pair reads as two
 * charges stacked on each other, which confuses patients. Only one
 * non-zero → show that one. Both zero → "Copay $0.00" (2026-10-01).
 */
interface BreakdownRow {
  label: string;
  value: string;
  /**
   * Layout slot: Deductible on the left, Copay/Coinsurance flush right
   * (lines up with the card's amounts), Insurance-pay on its own line.
   */
  slot: 'left' | 'right' | 'line';
}

function breakdownRows(data: SameDayAppointment): BreakdownRow[] {
  const rows: BreakdownRow[] = [];
  if (data.deductible_minor_units > 0) {
    rows.push({ label: 'Deductible', value: format(data.deductible_minor_units), slot: 'left' });
  }
  // Exactly one cost-share row, always: Copay if non-zero, else
  // Coinsurance if non-zero, else "Copay $0.00" so the patient sees the
  // visit has no copay rather than a silently missing row.
  if (data.copay_minor_units <= 0 && data.coinsurance !== null && !isCoinsuranceZeroOrEmpty(data.coinsurance)) {
    rows.push({ label: 'Coinsurance', value: formatCoinsurance(data.coinsurance), slot: 'right' });
  } else {
    rows.push({ label: 'Copay', value: format(Math.max(data.copay_minor_units, 0)), slot: 'right' });
  }
  if (data.insurance_estimated_pay_minor_units > 0) {
    rows.push({
      label: 'Insurance estimated to pay',
      value: format(data.insurance_estimated_pay_minor_units),
      slot: 'line',
    });
  }
  return rows;
}

function BreakdownBlock({ data }: { data: SameDayAppointment }) {
  const rows = breakdownRows(data);
  if (rows.length === 0) return null;

  // Each label sits right next to its value (not pushed to opposite edges
  // of a half-width column) — the wide gap made the pairs hard to read.
  // Copay/Coinsurance is pushed flush right so it lines up with the
  // amounts above and below it.
  const slotClass: Record<BreakdownRow['slot'], string> = {
    left: '',
    right: 'ml-auto',
    line: 'basis-full',
  };
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 border-b border-border bg-muted/20 px-4 py-4 sm:px-6 text-sm">
      {rows.map((r) => (
        <div key={r.label} className={`flex items-baseline gap-2 ${slotClass[r.slot]}`}>
          <dt className="text-muted-foreground">{r.label}</dt>
          <dd className="font-medium text-foreground tabular-nums">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Canonical case: "Due today" + the remaining amount on one line. The
 * checkbox itself lives in the header — this is the body of the card.
 */
function CheckboxRowSummary({ remainingCents }: { remainingCents: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <p className="text-sm text-muted-foreground">Due today</p>
      <div className="text-base font-semibold text-foreground tabular-nums">
        {format(remainingCents)}
      </div>
    </div>
  );
}

/**
 * Sub-$0.50 case: the remaining is real but below Stripe's minimum charge.
 * The patient can't settle it online — they'll pay at the clinic. Surface
 * it as an Alert rather than a checkbox so it's not interactable.
 */
function SubMinimumNotice({ remainingCents }: { remainingCents: number }) {
  return (
    <Alert variant="info" title={`Remaining ${format(remainingCents)} — please pay at the clinic`}>
      Amounts under {format(STRIPE_USD_MIN_CENTS)} can&rsquo;t be charged
      online. Please bring exact change or pay at the front desk when you
      arrive.
    </Alert>
  );
}

/**
 * Decision D3: a same-day visit without an estimate is shown, never
 * hidden. Nothing to pay online for it yet — the clinic may produce an
 * estimate later, or the patient settles it at check-in.
 */
function PendingNotice() {
  return (
    <Alert variant="info" title="Pending — no estimate yet">
      The clinic hasn&rsquo;t prepared an estimate for this visit. There is
      nothing to pay for it online right now; you can settle it at check-in
      or come back to this link later.
    </Alert>
  );
}

/**
 * Settled case (multi-visit days only): the estimate exists and nothing
 * remains. Shown so the patient can see the whole day is covered.
 */
function SettledNotice() {
  return (
    <Alert variant="success" title="Nothing more to pay for this visit">
      This visit&rsquo;s estimate is already covered.
    </Alert>
  );
}

// v0.8.4: `formatDateTime` helper removed. Pre-formatting now happens
// backend-side; FE renders `appointment_date_formatted` verbatim. Per
// backend v0.8.2 contract + FE ledger #49.
